import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { BASE_URL } from "../../../utils/config/config";
import axiosInstance from "../../../utils/config/config";
import { getPatient } from "../../../redux/actions/user.dispatcher";
import { getAllPractitioners } from "../../../redux/actions/practitioners.dispatcher";
import { getAllPayments } from "../../../redux/actions/payments.dispatcher";
import {
  getPractitionerName,
  getPatientEmail,
  filterValidAppointments,
  buildCalendarBookingUrl,
  calculateAppointmentCharge,
} from "./Patient.utils";

// Guard against corrupted hourlyRate values in the database
const getValidHourlyRate = (rate, fallback = 150) => {
  const parsed = parseFloat(rate);
  return parsed > 0 && parsed <= 10000 ? parsed : fallback;
};

const INITIAL_PAYMENT_MODAL = {
  open: false,
  appointment: null,
  cardInfo: null,
  success: false,
  paymentData: null,
};

const INITIAL_DETAILS_MODAL = {
  open: false,
  appointment: null,
};

const INITIAL_AGREEMENT_STATE = {
  signed: false,
  loading: true,
  agreement: null,
  modalOpen: false,
  isMinor: false,
};

// Telehealth Informed Consent has the same shape as the Participation
// Agreement state; kept as a separate slice so the two flows can evolve
// independently.
const INITIAL_TELEHEALTH_CONSENT_STATE = {
  signed: false,
  loading: true,
  consent: null,
  modalOpen: false,
  isMinor: false,
};

export const usePatientAppointments = () => {
  const { userData, patient, iphcPractitioner } = useSelector(
    (state) => state.user
  );
  const { practitioners, loading: practitionersLoading } = useSelector(
    (state) => state.practitioners
  );
  const { payments, loading: paymentsLoading } = useSelector(
    (state) => state.payments
  );

  const dispatch = useDispatch();
  const [appointments, setAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastAppointmentsLoading, setPastAppointmentsLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState(INITIAL_PAYMENT_MODAL);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [appointmentDetailsModal, setAppointmentDetailsModal] = useState(
    INITIAL_DETAILS_MODAL
  );
  const [cancelConfirm, setCancelConfirm] = useState({
    open: false,
    appointment: null,
  });
  const [rescheduleConfirm, setRescheduleConfirm] = useState({
    open: false,
    appointment: null,
  });
  const [calendarModal, setCalendarModal] = useState({
    open: false,
    calendarUrl: null,
    title: "",
    mode: null, // "follow-up" or "reschedule"
    appointment: null, // original appointment (for reschedule)
  });
  const [agreementState, setAgreementState] = useState(INITIAL_AGREEMENT_STATE);
  // For practitioners: per-client agreement status cache (email -> boolean)
  const [clientAgreementStatuses, setClientAgreementStatuses] = useState({});
  // Telehealth Informed Consent — mirrors the agreement state above.
  const [telehealthConsentState, setTelehealthConsentState] = useState(
    INITIAL_TELEHEALTH_CONSENT_STATE,
  );
  // For practitioners: per-client telehealth consent status cache (email -> boolean)
  const [clientTelehealthConsentStatuses, setClientTelehealthConsentStatuses] =
    useState({});

  const isPractitioner =
    userData?.role !== "patient" &&
    iphcPractitioner &&
    Object.keys(iphcPractitioner).length > 0;
  const isPatient = patient && Object.keys(patient).length > 0;

  const practitionerUserId = useMemo(() => {
    if (!userData?.email || practitioners.length === 0) return null;
    return (
      practitioners.find((p) => p.email === userData.email)?.userId ?? null
    );
  }, [userData?.email, practitioners]);

  const getPractitionerNameFn = (assignedUserId) =>
    getPractitionerName(assignedUserId, practitioners);

  const getPatientEmailFn = (appointment) =>
    getPatientEmail(appointment, patient);

  const resetPaymentModal = () => setPaymentModal(INITIAL_PAYMENT_MODAL);
  const resetDetailsModal = () =>
    setAppointmentDetailsModal(INITIAL_DETAILS_MODAL);

  const fetchPatientAppointments = useCallback(async (contactId, email) => {
    const payload = {};
    if (contactId) payload.contactId = contactId;
    if (email) payload.email = email;
    if (!payload.contactId && !payload.email) {
      setAppointments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        "user/patient-appointments",
        payload
      );
      const appointmentsList = response.data.success
        ? response.data.appointments
        : [];
      const validAppointments = filterValidAppointments(appointmentsList);
      const sorted = validAppointments.sort(
        (a, b) => new Date(a.startTime) - new Date(b.startTime)
      );
      setAppointments(sorted);
    } catch {
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPatientPastAppointments = useCallback(async (contactId, email) => {
    const payload = {};
    if (contactId) payload.contactId = contactId;
    if (email) payload.email = email;
    if (!payload.contactId && !payload.email) {
      setPastAppointments([]);
      setPastAppointmentsLoading(false);
      return;
    }

    setPastAppointmentsLoading(true);
    try {
      const response = await axiosInstance.post(
        "user/patient-appointments/past",
        payload
      );
      if (response.data.success) {
        const valid = filterValidAppointments(response.data.appointments || []);
        setPastAppointments(valid);
      } else {
        setPastAppointments([]);
      }
    } catch {
      setPastAppointments([]);
    } finally {
      setPastAppointmentsLoading(false);
    }
  }, []);

  const refreshPatientAppointments = useCallback(() => {
    if (patient?.contact?.id) {
      fetchPatientAppointments(patient.contact.id, patient.contact.email || userData?.email);
      fetchPatientPastAppointments(patient.contact.id, patient.contact.email || userData?.email);
    }
  }, [
    patient?.contact?.id,
    patient?.contact?.email,
    userData?.email,
    fetchPatientAppointments,
    fetchPatientPastAppointments,
  ]);

  const fetchPractitionerAppointments = useCallback(async (userId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `user/staff/${userId}/appointments`
      );
      if (response.data.success) {
        const valid = filterValidAppointments(response.data.appointments || []);
        const sorted = valid.sort(
          (a, b) => new Date(a.startTime) - new Date(b.startTime)
        );
        setAppointments(sorted);
      } else {
        setAppointments([]);
      }
    } catch {
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPractitionerPastAppointments = useCallback(async (userId) => {
    setPastAppointmentsLoading(true);
    try {
      const response = await axiosInstance.get(
        `user/staff/${userId}/appointments/past`
      );
      if (response.data.success) {
        const valid = filterValidAppointments(response.data.appointments || []);
        setPastAppointments(valid);
      } else {
        setPastAppointments([]);
      }
    } catch {
      setPastAppointments([]);
    } finally {
      setPastAppointmentsLoading(false);
    }
  }, []);

  const refreshPractitionerAppointments = useCallback(() => {
    if (!isPractitioner || !iphcPractitioner?.ihClinicId || !practitionerUserId) {
      return;
    }
    fetchPractitionerAppointments(practitionerUserId);
    fetchPractitionerPastAppointments(practitionerUserId);
  }, [
    isPractitioner,
    iphcPractitioner?.ihClinicId,
    practitionerUserId,
    fetchPractitionerAppointments,
    fetchPractitionerPastAppointments,
  ]);

  const handleConfirmCharge = async () => {
    try {
      setProcessingPayment(true);
      setPaymentError(null);

      const { appointment, calculatedAmount } = paymentModal;
      const practitionerName = getPractitionerNameFn(
        appointment.assignedUserId
      );
      const patientEmail = getPatientEmailFn(appointment);

      if (!patientEmail) throw new Error("Client email not found");

      let patientUserId = appointment.contact?.userId;
      if (!patientUserId) {
        const res = await axios.get(
          `${BASE_URL}user/find-by-email?email=${patientEmail}`
        );
        patientUserId = res.data.user?.id;
      }
      if (!patientUserId) throw new Error("Client user ID not found");

      const chargeResponse = await axios.post(
        `${BASE_URL}payments/charge-customer`,
        {
          email: patientEmail,
          amount: calculatedAmount,
          description: `Consultation - ${new Date(
            appointment.startTime
          ).toLocaleDateString()} (${
            paymentModal.appointmentDuration
          } minutes)`,
          appointmentId: appointment.id,
          userId: patientUserId,
          practitionerName,
          practitionerId: iphcPractitioner?.id || null,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!chargeResponse.data.success) {
        throw new Error(chargeResponse.data.message || "Payment failed");
      }

      dispatch(getAllPayments());
      setPaymentModal({
        ...paymentModal,
        success: true,
        paymentData: chargeResponse.data.data,
      });
    } catch (error) {
      setPaymentError(
        error.response?.data?.message ||
          error.message ||
          "Payment failed. Please try again."
      );
      resetPaymentModal();
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleClosePaymentModal = () => {
    if (!processingPayment) resetPaymentModal();
  };

  const handleOpenChargeModal = async (appointment) => {
    try {
      setPaymentError(null);
      const patientEmail = getPatientEmailFn(appointment);

      if (!patientEmail) {
        setPaymentError("Client email not found for this session.");
        return;
      }

      // Check if patient has a verified card on file
      const { data } = await axiosInstance.get(
        `payments/check-verified-card?email=${encodeURIComponent(patientEmail)}`
      );

      if (!data.success || !data.verified) {
        setPaymentError(
          "This client does not have a verified payment card on file. They must verify their card on the Nirva website before they can be charged."
        );
        return;
      }

      // Calculate charge based on appointment duration
      const hourlyRate = getValidHourlyRate(iphcPractitioner?.hourlyRate);
      const { durationInMinutes, chargeAmount } = calculateAppointmentCharge(
        appointment.startTime,
        appointment.endTime,
        hourlyRate
      );

      setPaymentModal({
        open: true,
        appointment,
        cardInfo: {
          cardBrand: data.card?.cardBrand || "Card",
          last4: data.card?.last4 || "****",
        },
        calculatedAmount: chargeAmount,
        appointmentDuration: durationInMinutes,
        success: false,
        paymentData: null,
      });
    } catch (error) {
      setPaymentError(
        error.response?.data?.message ||
          error.message ||
          "Failed to verify payment card. Please try again."
      );
    }
  };

  const handleBookFollowUp = (appointment) => {
    const practitionerId = appointment.assignedUserId;
    if (!practitionerId) {
      setPaymentError("Practitioner information not found");
      return;
    }

    const practitioner = practitioners.find((p) => p.userId === practitionerId);
    if (!practitioner) {
      setPaymentError("Practitioner not found");
      return;
    }

    const calendarUrl = buildCalendarBookingUrl(
      practitioner,
      patient,
      appointment
    );
    if (!calendarUrl) {
      setPaymentError("No calendar found for this practitioner");
      return;
    }

    setPaymentError(null);
    setCalendarModal({
      open: true,
      calendarUrl,
      title: "Schedule Follow-Up Session",
      mode: "follow-up",
      appointment: null,
    });
  };

  const handleRequestCancel = (appointment) => {
    setCancelConfirm({ open: true, appointment });
  };

  const handleDismissCancel = () => {
    setCancelConfirm({ open: false, appointment: null });
  };

  const handleConfirmCancel = async () => {
    const appointment = cancelConfirm.appointment;
    if (!appointment) return;

    try {
      setCancelConfirm({ open: false, appointment: null });

      await axios.put(`${BASE_URL}gohighlevel/events/${appointment.id}`, {
        calendarId: appointment.calendarId,
        title: appointment.title,
        appointmentStatus: "cancelled",
        startTime: appointment.startTime,
        endTime: appointment.endTime,
        // Context for cancellation notifications
        contactId: appointment.contactId,
        assignedUserId: appointment.assignedUserId,
        cancelledBy: isPractitioner ? "practitioner" : "patient",
      });

      // Refresh appointments list
      if (isPractitioner) {
        refreshPractitionerAppointments();
      } else if (isPatient && patient?.contact?.id) {
        refreshPatientAppointments();
      }
    } catch (error) {
      setPaymentError(
        error.response?.data?.message ||
          error.message ||
          "Failed to cancel session. Please try again."
      );
    }
  };

  const handleReschedule = (appointment) => {
    const practitionerId = appointment.assignedUserId;
    if (!practitionerId) {
      setPaymentError("Practitioner information not found");
      return;
    }

    const practitioner = practitioners.find((p) => p.userId === practitionerId);
    if (!practitioner) {
      setPaymentError("Practitioner not found");
      return;
    }

    const calendarUrl = buildCalendarBookingUrl(
      practitioner,
      patient,
      appointment
    );
    if (!calendarUrl) {
      setPaymentError("No calendar found for this practitioner");
      return;
    }

    setPaymentError(null);
    setCalendarModal({
      open: true,
      calendarUrl,
      title: "Reschedule Appointment",
      mode: "reschedule",
      appointment,
    });
  };

  const handleCloseCalendarModal = () => {
    const { mode, appointment } = calendarModal;
    setCalendarModal({
      open: false,
      calendarUrl: null,
      title: "",
      mode: null,
      appointment: null,
    });

    // Refresh appointments regardless of mode
    if (isPractitioner) {
      refreshPractitionerAppointments();
    } else if (isPatient && patient?.contact?.id) {
      refreshPatientAppointments();
    }
    dispatch(getAllPayments());

    // For reschedule, ask the user if they want to cancel the original
    if (mode === "reschedule" && appointment) {
      setRescheduleConfirm({ open: true, appointment });
    }
  };

  const handleCalendarBookingDetected = () => {
    // Booking was detected via postMessage from the GHL iframe.
    // Close the modal and refresh.
    const { mode, appointment } = calendarModal;
    setCalendarModal({
      open: false,
      calendarUrl: null,
      title: "",
      mode: null,
      appointment: null,
    });

    // Refresh appointments
    if (isPractitioner) {
      refreshPractitionerAppointments();
    } else if (isPatient && patient?.contact?.id) {
      refreshPatientAppointments();
    }
    dispatch(getAllPayments());

    // For reschedule, automatically show the confirmation to cancel original
    if (mode === "reschedule" && appointment) {
      setRescheduleConfirm({ open: true, appointment });
    }
  };

  const handleConfirmReschedule = async () => {
    const appointment = rescheduleConfirm.appointment;
    if (!appointment) return;

    try {
      setRescheduleConfirm({ open: false, appointment: null });

      const existingNotes = appointment.notes || "";
      const rescheduledNotes = existingNotes
        ? `${existingNotes}\nRescheduled`
        : "Rescheduled";

      await axios.put(
        `${BASE_URL}gohighlevel/events/${appointment.id}`,
        {
          calendarId: appointment.calendarId,
          title: appointment.title,
          appointmentStatus: "cancelled",
          startTime: appointment.startTime,
          endTime: appointment.endTime,
          notes: rescheduledNotes,
          contactId: appointment.contactId,
          assignedUserId: appointment.assignedUserId,
        }
      );

      // Refresh appointments after cancelling original
      if (isPractitioner) {
        refreshPractitionerAppointments();
      } else if (isPatient && patient?.contact?.id) {
        refreshPatientAppointments();
      }
      dispatch(getAllPayments());
    } catch (error) {
      setPaymentError(
        error.response?.data?.message ||
          error.message ||
          "Failed to cancel original session. Please cancel it manually."
      );
    }
  };

  const handleDismissReschedule = () => {
    setRescheduleConfirm({ open: false, appointment: null });
  };

  const handleViewAppointmentDetails = (appointment) => {
    setAppointmentDetailsModal({ open: true, appointment });

    // For practitioners: fetch the client's agreement status
    if (isPractitioner && appointment?.contact?.email) {
      const clientEmail = appointment.contact.email;
      // Only fetch if we haven't already cached it
      if (clientAgreementStatuses[clientEmail] === undefined) {
        axiosInstance
          .get(`agreements/check-by-email?email=${encodeURIComponent(clientEmail)}`)
          .then((response) => {
            if (response.data.success) {
              setClientAgreementStatuses((prev) => ({
                ...prev,
                [clientEmail]: response.data.signed,
              }));
            }
          })
          .catch(() => {
            // Silently fail -- badge just won't show
          });
      }
      // And the telehealth consent status (separate cache, same pattern)
      if (clientTelehealthConsentStatuses[clientEmail] === undefined) {
        axiosInstance
          .get(
            `telehealth-consents/check-by-email?email=${encodeURIComponent(clientEmail)}`,
          )
          .then((response) => {
            if (response.data.success) {
              setClientTelehealthConsentStatuses((prev) => ({
                ...prev,
                [clientEmail]: response.data.signed,
              }));
            }
          })
          .catch(() => {
            // Silently fail -- badge just won't show
          });
      }
    }
  };

  const handleCloseAppointmentDetails = () => resetDetailsModal();

  const fetchAgreementStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get("agreements/status");
      if (response.data.success) {
        setAgreementState((prev) => ({
          ...prev,
          signed: response.data.signed,
          agreement: response.data.agreement || null,
          isMinor: response.data.isMinor || false,
          loading: false,
        }));
      }
    } catch {
      setAgreementState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const handleOpenAgreementModal = () => {
    setAgreementState((prev) => ({ ...prev, modalOpen: true }));
  };

  const handleCloseAgreementModal = () => {
    setAgreementState((prev) => ({ ...prev, modalOpen: false }));
  };

  const handleAgreementSigned = (agreement) => {
    setAgreementState((prev) => ({
      ...prev,
      signed: true,
      agreement,
      modalOpen: false,
    }));
  };

  // ---------- Telehealth Informed Consent ----------

  const fetchTelehealthConsentStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get("telehealth-consents/status");
      if (response.data.success) {
        setTelehealthConsentState((prev) => ({
          ...prev,
          signed: response.data.signed,
          consent: response.data.consent || null,
          isMinor: response.data.isMinor || false,
          loading: false,
        }));
      }
    } catch {
      setTelehealthConsentState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const handleOpenTelehealthConsentModal = () => {
    setTelehealthConsentState((prev) => ({ ...prev, modalOpen: true }));
  };

  const handleCloseTelehealthConsentModal = () => {
    setTelehealthConsentState((prev) => ({ ...prev, modalOpen: false }));
  };

  const handleTelehealthConsentSigned = (consent) => {
    setTelehealthConsentState((prev) => ({
      ...prev,
      signed: true,
      consent,
      modalOpen: false,
    }));
  };

  useEffect(() => {
    dispatch(getAllPayments());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllPractitioners());
  }, [dispatch]);

  // Fetch agreement status for patients
  useEffect(() => {
    if (userData?.id && !isPractitioner) {
      fetchAgreementStatus();
    } else {
      setAgreementState((prev) => ({ ...prev, loading: false, signed: true }));
    }
  }, [userData?.id, isPractitioner, fetchAgreementStatus]);

  // Fetch telehealth consent status for patients
  useEffect(() => {
    if (userData?.id && !isPractitioner) {
      fetchTelehealthConsentStatus();
    } else {
      setTelehealthConsentState((prev) => ({
        ...prev,
        loading: false,
        signed: true,
      }));
    }
  }, [userData?.id, isPractitioner, fetchTelehealthConsentStatus]);

  useEffect(() => {
    if (userData?.id && !isPractitioner) {
      dispatch(getPatient(userData.id));
    }
  }, [userData?.id, isPractitioner, dispatch]);

  useEffect(() => {
    if (!userData?.email || practitioners.length === 0) {
      setLoading(false);
      return;
    }

    if (isPractitioner && iphcPractitioner?.ihClinicId) {
      if (practitionerUserId) {
        refreshPractitionerAppointments();
      } else {
        setLoading(false);
      }
    } else if (isPatient && patient?.contact?.id) {
      refreshPatientAppointments();
    } else {
      setLoading(false);
    }
  }, [
    isPractitioner,
    isPatient,
    patient?.contact?.id,
    patient?.contact?.email,
    userData?.email,
    practitioners.length,
    iphcPractitioner?.ihClinicId,
    practitionerUserId,
    refreshPatientAppointments,
    refreshPractitionerAppointments,
  ]);

  const isLoading = loading || practitionersLoading || paymentsLoading;

  return {
    appointments,
    pastAppointments,
    pastAppointmentsLoading,
    patient,
    iphcPractitioner,
    practitioners,
    payments,
    isPractitioner,
    isPatient,
    isLoading,
    paymentModal,
    processingPayment,
    paymentError,
    setPaymentError,
    appointmentDetailsModal,
    cancelConfirm,
    rescheduleConfirm,
    calendarModal,
    agreementState,
    clientAgreementStatuses,
    telehealthConsentState,
    clientTelehealthConsentStatuses,

    getPractitionerNameFn,
    handleConfirmCharge,
    handleClosePaymentModal,
    handleOpenChargeModal,
    handleBookFollowUp,
    handleViewAppointmentDetails,
    handleCloseAppointmentDetails,
    handleRequestCancel,
    handleDismissCancel,
    handleConfirmCancel,
    handleReschedule,
    handleConfirmReschedule,
    handleDismissReschedule,
    handleCloseCalendarModal,
    handleCalendarBookingDetected,
    handleOpenAgreementModal,
    handleCloseAgreementModal,
    handleAgreementSigned,
    handleOpenTelehealthConsentModal,
    handleCloseTelehealthConsentModal,
    handleTelehealthConsentSigned,
  };
};
